import React, { useContext, useEffect, useState } from 'react';
import { WelletContext } from '../../../context/WelletContext';
import { useDispatch, useSelector } from 'react-redux';
import { getDateFromOffset } from '../../../helpers/dates';
import { setDateRange } from '../../../actions/reservationActions';
import { getReservations } from '../../../services/reservations/reservationsService';
import Header from '../../../components/Common/Header/Header';
import ContentApp from '../../../components/ContentApp';
import { CircleSpinner } from 'react-spinners-kit';
import EmptyActivity from '../../../components/Common/EmptyActivity/EmptyActivity';
import ActivityReserationItem from '../../../components/Activities/ActivityReservationItem.js/ActivityReservationItem';

import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';
import PaymentLinkItem from '../../../components/PaymentLinkItem/PaymentLinkItem';

const PaymentsLink = ({ history }) => {
  const { t } = useTranslation();

  const welletContext = useContext(WelletContext);
  const [selectedFilters, setSelectedFilters] = useState(null);
  const [reservations, setReservations] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [skip, setSkip] = useState(15);
  const [error, setError] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [amountFrom, setAmountFrom] = useState(null);
  const [amountTo, setAmountTo] = useState(null);
  const user = useSelector((state) => state.associates.user);
  const savedDates = useSelector((state) => state.reservation.dateRange[0]);
  const dispatch = useDispatch();
  const lastIndex = reservations ? reservations.length - 1 : -1;
  const savedFilter = localStorage.getItem('filterPaymentLink');

  const getStatus = () => {
    switch (savedFilter) {
      case 'PAID':
        return ['PAID'];
      default:
        return [];
    }
  };

  const requestBody = (more = false) => {
    const fromDate = savedDates.startDate
      ? format(savedDates.startDate, 'yyyy-MM-dd')
      : format(initialDates[0].startDate, 'yyyy-MM-dd');
    const toDate = savedDates.endDate
      ? format(savedDates.endDate, 'yyyy-MM-dd')
      : format(initialDates[0].endDate, 'yyyy-MM-dd');

    return {
      cityId: null,
      showId: null,
      reservationType: selectedFilters?.reservationType?.type ?? '',
      sortBy: 'CREATED_DATE_DESC',
      status: getStatus(),
      from: fromDate,
      to: toDate,
      limit: 15,
      skip: more ? skip : 0,
      filterField: null,
      amountFrom: amountFrom,
      amountTo: amountTo,
    };
  };

  const getInitialDates = () => {
    const today = new Date(getDateFromOffset(user.companyUtcMinutesOffset));
    today.setHours(0, 0, 0, 0);
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - 7);

    return [
      {
        startDate: savedFilter === 'RESERVATION_DATE' ? today : startDate,
        endDate: today,
        key: 'selection',
      },
    ];
  };

  const [initialDates, setInitialDates] = useState(getInitialDates());

  useEffect(() => {
    if (!savedDates.startDate) dispatch(setDateRange(initialDates));
  }, []);

  const fetchReservations = async (more = false) => {
    if (!hasMore && more) {
      return;
    }
    if (more) setLoadingMore(true);
    else setLoading(true);

    const body = requestBody(more);

    try {
      const reservationsData = await getReservations(welletContext, body);
      if (more) {
        if (
          reservationsData.length === 0 ||
          JSON.stringify(reservations.slice(-15)) === JSON.stringify(reservationsData)
        ) {
          setHasMore(false);
        } else {
          setReservations((prevReservations) => [...prevReservations, ...reservationsData]);
          setSkip((prevSkip) => prevSkip + reservationsData.length);
          setHasMore(reservationsData.length === 15);
        }
      } else {
        setReservations(reservationsData);
      }
    } catch (error) {
      console.error(error);
      setError(t('callApiError'));
    } finally {
      if (more) setLoadingMore(false);
      else setLoading(false);
    }
  };

  const fetchMoreReservations = async () => {
    if (!hasMore || loadingMore) return;
    setLoadingMore(true);

    const body = requestBody(true);

    try {
      const reservationsData = await getReservations(welletContext, body);
      if (reservationsData.length === 0) {
        setHasMore(false);
      } else {
        setReservations((prevReservations) => [...prevReservations, ...reservationsData]);
        setSkip((prevSkip) => prevSkip + reservationsData.length);
      }
    } catch (error) {
      console.error(error);
      setError(t('callApiError'));
    } finally {
      setLoadingMore(false);
    }
  };

  const debounce = function (func, delay) {
    let timeout;
    return function (...args) {
      clearTimeout(timeout);
      timeout = setTimeout(() => func.apply(this, args), delay);
    };
  };

  useEffect(() => {
    fetchReservations();
  }, []);

  useEffect(() => {
    const handleScroll = debounce(() => {
      const reachedBottom =
        window.innerHeight + document.documentElement.scrollTop + 1 >=
        document.documentElement.offsetHeight;
      if (reachedBottom && hasMore && !loading && !loadingMore) {
        fetchMoreReservations();
      }
    }, 100);

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [loading, loadingMore, hasMore, skip]);

  return (
    <>
      <Header title={t('homeLinkTitle')} hasBackground={true} showBack={true} />

      <ContentApp>
        {error ? (
          <div className='mt-2'>
            <EmptyActivity subtitle={error} />
          </div>
        ) : (
          <>
            {loading ? (
              <div className='loaderDiv'>
                <CircleSpinner
                  className='spinner-app mx-auto'
                  style={{ margin: 'auto' }}
                  size={40}
                  color='var(--color-font-primary)'
                />
              </div>
            ) : (
              <>
                {reservations?.length === 0 ? (
                  <div className='mt-2'>
                    <EmptyActivity title={t('noPaymentLinks')} />
                  </div>
                ) : (
                  <div className='mt-2'>
                    {reservations?.map((r, index) => (
                      <PaymentLinkItem
                        activity={r}
                        date={r.createdDate}
                        rightText={r.amount}
                        index={index}
                        lastIndex={reservations.length - 1}
                       handleClick={() => history.push(`/link-details?id=${r.id}`)}
                      />
                    ))}
                    {loadingMore && hasMore && (
                      <div className='p-3 d-flex justify-content-center'>
                        <CircleSpinner
                          className='spinner-app mx-auto'
                          style={{ margin: 'auto' }}
                          size={40}
                          color='var(--color-font-primary)'
                        />
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </>
        )}
      </ContentApp>
    </>
  );
};

export default PaymentsLink;
